import React, { useEffect, useMemo, useState, Component } from 'react';
import { SocialLink, ContactData } from '../types';
import { ContentService } from '../services/content';
const Contact: React.FC = () => {
  const [contactData, setContactData] = useState<ContactData | null>(null);
  const [socials, setSocials] = useState<SocialLink[]>([]);
  const [loading, setLoading] = useState(true);
  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  // Bot Honeypot
  const [honeypot, setHoneypot] = useState('');
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [data, socialLinks] = await Promise.all([ContentService.getContactData(), ContentService.getSocialLinks()]);
        setContactData(data);
        setSocials(socialLinks);
      } catch (err) {
        console.error('Failed to sync with contact_data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Honeypot check: if filled, it's a bot
    if (honeypot) {
      console.warn('Bot detected.');
      setIsSuccess(true);
      return;
    }
    if (!name || !email || !message) return;
    setIsSubmitting(true);
    setIsError(false);
    const metadata = {
      userAgent: navigator.userAgent,
      screenResolution: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      platform: navigator.platform,
      timestamp: new Date().toISOString()
    };
    const success = await ContentService.submitContactMessage({
      name,
      email,
      message,
      metadata
    });
    if (success) {
      setIsSuccess(true);
    } else {
      setIsError(true);
    }
    setIsSubmitting(false);
  };
  const handleReset = () => {
    setIsSuccess(false);
    setName('');
    setEmail('');
    setMessage('');
    setHoneypot('');
  };
  const whatsappUrl = useMemo(() => {
    const phone = contactData?.whatsapp || '91 9743996505';
    const text = encodeURIComponent(`Hi, I'm ${name}.\n\n${message}\n\nMy email: ${email}`);
    return `https://wa.me/${phone.replace(/\+/g, '')}?text=${text}`;
  }, [contactData, name, message, email]);
  const mailtoUrl = useMemo(() => {
    const targetEmail = contactData?.email || 'shylash.k@gmail.com';
    const subject = encodeURIComponent(`Inquiry from ${name}`);
    const body = encodeURIComponent(`${message}\n\nSent from: ${name} (${email})`);
    return `mailto:${targetEmail}?subject=${subject}&body=${body}`;
  }, [contactData, name, message, email]);
  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center" data-id="element-150">
        <div className="flex flex-col items-center gap-4" data-id="element-151">
          <div className="w-8 h-8 border-2 border-white/10 border-t-white rounded-full animate-spin" data-id="element-152" />
          <span className="text-[10px] uppercase tracking-[0.4em] text-zinc-600 font-bold italic" data-id="element-153">
            accessing contact_engines...
          </span>
        </div>
      </div>;
  return <div className="max-w-screen-2xl mx-auto px-6 md:px-12 pt-[104px] md:pt-32 pb-24 min-h-screen" data-id="element-154">
      <header className="mb-10 md:mb-24" data-id="element-155">
        <h1 className="text-5xl md:text-9xl font-extrabold tracking-tighter mb-6 md:mb-12 lowercase opacity-90 flex items-center gap-6" data-id="element-156">
          <span className="animate-[wave_2.5s_infinite] origin-[70%_70%]" data-id="element-157">
            👋
          </span>
          {contactData?.title || 'say hello'}
        </h1>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12" data-id="element-158">
          <div className="md:col-span-8" data-id="element-159">
            <h2 className="text-3xl md:text-6xl leading-[1.1] tracking-tighter font-medium lowercase max-w-5xl" data-id="element-160">
              {"let's collaborate. feel free to drop me a line about your project."}
            </h2>
            {contactData?.availability_note && <p className="mt-8 md:mt-12 text-zinc-500 text-sm uppercase tracking-[0.3em] font-black inline-flex items-center gap-3" data-id="element-161">
                available for new projects.
              </p>}
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-24" data-id="element-162">
        <div className="lg:col-span-8" data-id="element-163">
          {isSuccess ? <div className="bg-zinc-900/30 p-10 md:p-24 rounded-sm border border-white/10 text-center animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-164">
              <span className="text-6xl block mb-6 md:mb-8" data-id="element-165">✨</span>
              <h3 className="text-4xl font-extrabold tracking-tighter mb-4 lowercase" data-id="element-166">
                message captured
              </h3>
              <p className="text-zinc-500 text-xl font-medium mb-10 md:mb-12 lowercase" data-id="element-167">
                want to speed up the conversation?
              </p>

              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-10 md:mb-12" data-id="element-168">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="w-full md:w-auto px-10 py-5 bg-[#25D366] text-black font-black uppercase tracking-[0.2em] text-[10px] rounded-sm hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 cursor-target" data-id="element-169">
                  <span data-id="element-170">chat on whatsapp</span>
                </a>
                <a href={mailtoUrl} className="w-full md:w-auto px-10 py-5 bg-white text-black font-black uppercase tracking-[0.2em] text-[10px] rounded-sm hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-3 cursor-target" data-id="element-171">
                  <span data-id="element-172">send direct email</span>
                </a>
              </div>
              <button onClick={handleReset} className="text-[10px] font-black uppercase tracking-widest text-zinc-600 hover:text-white transition-all cursor-target" data-id="element-173">
                ← back to form
              </button>
            </div> : <form className="space-y-6" onSubmit={handleSubmit} data-id="element-174">
              {/* Honeypot Field */}
              <input type="text" name="bot_field" style={{
            display: 'none'
          }} tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} data-id="element-175" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-176">
                <div className="space-y-2" data-id="element-177">
                  <label className="text-[10px] text-zinc-600 uppercase tracking-widest ml-4 font-black" data-id="element-178">
                    .name
                  </label>
                  <input required type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-zinc-900/50 border border-white/5 px-6 py-5 rounded-sm focus:ring-1 focus:ring-white outline-none transition-all placeholder:text-zinc-700" placeholder="Your name" data-id="element-179" />
                </div>
                <div className="space-y-2" data-id="element-180">
                  <label className="text-[10px] text-zinc-600 uppercase tracking-widest ml-4 font-black" data-id="element-181">
                    .email
                  </label>
                  <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-zinc-900/50 border border-white/5 px-6 py-5 rounded-sm focus:ring-1 focus:ring-white outline-none transition-all placeholder:text-zinc-700" placeholder="Your email" data-id="element-182" />
                </div>
              </div>
              <div className="space-y-2" data-id="element-183">
                <label className="text-[10px] text-zinc-600 uppercase tracking-widest ml-4 font-black" data-id="element-184">
                  .message
                </label>
                <textarea required rows={8} value={message} onChange={e => setMessage(e.target.value)} className="w-full bg-zinc-900/50 border border-white/5 px-6 py-5 rounded-sm focus:ring-1 focus:ring-white outline-none resize-none transition-all placeholder:text-zinc-700" placeholder="Tell me about your project or vision..." data-id="element-185" />
              </div>
              <button disabled={isSubmitting} className="w-full bg-white text-black font-black uppercase tracking-[0.2em] text-xs py-6 rounded-sm hover:bg-zinc-200 transition-all cursor-target shadow-2xl active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-186">
                {isSubmitting ? 'storing data...' : 'send message'}
              </button>
              {isError && <p className="text-red-500 text-[10px] font-black uppercase tracking-widest text-center mt-4" data-id="element-187">
                  failed to transmit. please try again.
                </p>}
            </form>}
        </div>
        <div className="lg:col-span-4 space-y-6 md:space-y-10" data-id="element-188">
          <div className="mb-6 md:mb-8" data-id="element-189">
            <span className="text-[10px] text-zinc-600 uppercase tracking-[0.3em] font-black block mb-4" data-id="element-190">
              .networks
            </span>
            <div className="h-px w-full bg-white/10" data-id="element-191"></div>
          </div>

          <div className="space-y-4" data-id="element-192">
            {socials.map((social, idx) => <a key={idx} href={social.url} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between p-6 bg-zinc-900/20 border border-white/5 hover:border-white/20 hover:bg-zinc-900/40 transition-all rounded-sm cursor-target" data-id="element-193">
                <div className="flex items-center space-x-6" data-id="element-194">
                  <span className="text-3xl grayscale group-hover:grayscale-0 transition-all" data-id="element-195">
                    {social.icon}
                  </span>
                  <div data-id="element-196">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-widest block font-black group-hover:text-white transition-colors" data-id="element-197">
                      {social.platform}
                    </span>
                    <span className="text-xl font-bold tracking-tight lowercase" data-id="element-198">
                      {social.handle}
                    </span>
                  </div>
                </div>
                <div className="text-xl opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-2 group-hover:translate-x-0" data-id="element-199">
                  →
                </div>
              </a>)}
          </div>
          <div className="pt-10 md:pt-12 mt-10 md:mt-12 border-t border-white/5" data-id="element-200">
            <span className="text-[10px] text-zinc-600 uppercase tracking-widest block font-black mb-4" data-id="element-201">
              Direct Mail
            </span>
            <a href={`mailto:${contactData?.email || 'shylash.k@gmail.com'}`} className="text-2xl md:text-3xl font-extrabold tracking-tighter hover:text-zinc-400 transition-colors cursor-target lowercase" data-id="element-202">
              {contactData?.email || 'shylash.k@gmail.com'}
            </a>
          </div>
        </div>
      </div>
      <style data-id="element-203">{`
        @keyframes wave {
          0% { transform: rotate( 0.0deg) }
          10% { transform: rotate(14.0deg) }
          20% { transform: rotate(-8.0deg) }
          30% { transform: rotate(14.0deg) }
          40% { transform: rotate(-4.0deg) }
          50% { transform: rotate(10.0deg) }
          60% { transform: rotate( 0.0deg) }
          100% { transform: rotate( 0.0deg) }
        }
      `}</style>
    </div>;
};
export default Contact;